import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { useTooltipProvider } from './TooltipProvider.js';

export type OpenOptions = {
  instant?: boolean;
};

export type CloseOptions = {
  instant?: boolean;
};

export type TooltipContextType = {
  close: (options?: CloseOptions) => void;
  id: string;
  isOpen: boolean;
  open: (options?: OpenOptions) => void;
  tooltipRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
};

export const TooltipContext = createContext<TooltipContextType | undefined>(
  undefined,
);

const DEFAULT_OPEN_DELAY = 700;
const DEFAULT_CLOSE_DELAY = 150;

export type TooltipRootProps = {
  children: ReactNode;
  closeDelay?: number;
  onClose?: () => void;
  onOpen?: () => void;
  openDelay?: number;
};

export const TooltipRoot = (props: TooltipRootProps) => {
  const { children, closeDelay = DEFAULT_CLOSE_DELAY, onClose, onOpen } = props;
  const provider = useTooltipProvider();

  const resolvedOpenDelay =
    props.openDelay ?? provider?.delayDuration ?? DEFAULT_OPEN_DELAY;

  const id = useId();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  onOpenRef.current = onOpen;
  onCloseRef.current = onClose;

  const openDelayRef = useRef(resolvedOpenDelay);
  const closeDelayRef = useRef(closeDelay);
  openDelayRef.current = resolvedOpenDelay;
  closeDelayRef.current = closeDelay;

  const open = useCallback(
    (options?: OpenOptions) => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (isOpenRef.current) return;

      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      const shouldSkipDelay =
        options?.instant || (provider?.isWithinSkipDelay() ?? false);
      const delay = shouldSkipDelay ? 0 : openDelayRef.current;

      openTimerRef.current = setTimeout(() => {
        isOpenRef.current = true;
        setIsOpen(true);
        onOpenRef.current?.();
        provider?.notifyOpen();
        openTimerRef.current = null;
      }, delay);
    },
    [provider],
  );

  const close = useCallback(
    (options?: CloseOptions) => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      if (!isOpenRef.current) return;

      if (options?.instant) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        isOpenRef.current = false;
        setIsOpen(false);
        onCloseRef.current?.();
        provider?.notifyClose();
        return;
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      closeTimerRef.current = setTimeout(() => {
        isOpenRef.current = false;
        setIsOpen(false);
        onCloseRef.current?.();
        provider?.notifyClose();
        closeTimerRef.current = null;
      }, closeDelayRef.current);
    },
    [provider],
  );

  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const contextValue = useMemo(
    () => ({ close, id, isOpen, open, tooltipRef, triggerRef }),
    [close, id, isOpen, open],
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextType => {
  const context = useContext(TooltipContext);

  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipRoot');
  }

  return context;
};
